import { AxiosResponse } from "axios";
import { axiosInstance, MessageResponse } from "./common";
import { VerificationGroupType } from "./AnalysisStep";

export interface UpdateVerificationRequest {
    current_details?: VerificationCurrentDetails |null;
    evidence_link?: string | null;
}

export interface OnboardingVerificationDetails {
  verified_by?: string | null; //null allowed
  verification_status?: VerificationStatus | null;
  verification_date?: string | null;
  remarks?: string | null;
  pre_login_valuation?: number | null;

  // Extra backend fields (Pydantic extra='allow')
  [key: string]: any;
}

export interface VerificationCurrentDetails {
  verification_status?: VerificationStatus | null;
  current_valuation?: number | null;
}

export interface AddVerification {
  loan_reference_id: string;
  customer_id?: string | null;
  group: VerificationGroupType
  document_type: string;
  document_id: string;
  onboarding_details?: OnboardingVerificationDetails;
  current_details?: VerificationCurrentDetails;
  evidence_link?: string | null;
}

export interface AddVerificationsRequest {
  verifications: AddVerification[];
}


export enum IdentityProfileDocumentType {
  PAN = "PAN",
  AADHAAR = "AADHAAR",
  VOTER_ID = "VOTER_ID",
  PASSPORT = "PASSPORT",
  DRIVING_LICENSE = "DRIVING_LICENSE",
  UDYAM_CERTIFICATE = "UDYAM_CERTIFICATE",
  CREDIT_REPORT = "CREDIT_REPORT",
  BANK_STATEMENT = "BANK_STATEMENT",
  BANK_ACCOUNT = "BANK_ACCOUNT",
  EMPLOYMENT = "EMPLOYMENT",
  INCOME = "INCOME",
  RESIDENCE_ADDRESS = "RESIDENCE_ADDRESS",
  BUSINESS_ADDRESS = "BUSINESS_ADDRESS",
  RATION_CARD = "RATION_CARD",
  UAN = "UAN",
  EPFO = "EPFO",
  ITR = "ITR",
  FSSAI = "FSSAI",
  CERSAI = "CERSAI",
  BUILDING_SANCTION_PLAN = "BUILDING_SANCTION_PLAN",
  TRADE_LICENSE = "TRADE_LICENSE",
  GST = "GST",
  ELECTRICITY_BILL = "ELECTRICITY_BILL",
  AS26 = "AS26",
  CIN = "CIN",
  RENT_AGREEMENT = "RENT_AGREEMENT",
  CA_CERTIFICATE = "CA_CERTIFICATE",
  SHOP_ACT_CERTIFICATE = "SHOP_ACT_CERTIFICATE",
  MCA = "MCA",
}

export enum DocumentType {
  PAN = "PAN",
  AADHAAR = "AADHAAR",
  VOTER_ID = "VOTER_ID",
  PASSPORT = "PASSPORT",
  DRIVING_LICENSE = "DRIVING_LICENSE",
  UDYAM_CERTIFICATE = "UDYAM_CERTIFICATE",
  CREDIT_REPORT = "CREDIT_REPORT",
  BANK_STATEMENT = "BANK_STATEMENT",
  BANK_ACCOUNT = "BANK_ACCOUNT",
  EMPLOYMENT = "EMPLOYMENT",
  INCOME = "INCOME",
  RESIDENCE_ADDRESS = "RESIDENCE_ADDRESS",
  BUSINESS_ADDRESS = "BUSINESS_ADDRESS",
  RATION_CARD = "RATION_CARD",
  UAN = "UAN",
  EPFO = "EPFO",
  ITR = "ITR",
  FSSAI = "FSSAI",
  BUILDING_SANCTION_PLAN = "BUILDING_SANCTION_PLAN",
  TRADE_LICENSE = "TRADE_LICENSE",
  GST = "GST",
  ELECTRICITY_BILL = "ELECTRICITY_BILL",
  AS26 = "AS26",
  CIN = "CIN",
  RENT_AGREEMENT = "RENT_AGREEMENT",
  CA_CERTIFICATE = "CA_CERTIFICATE",
  SHOP_ACT_CERTIFICATE = "SHOP_ACT_CERTIFICATE",
  MCA = "MCA",
  PROPERTY_VALUATION = "PROPERTY_VALUATION",
  VEHICLE_VALUATION = "VEHICLE_VALUATION",
  GIFT_DEED = "GIFT_DEED",
  SALE_DEED = "SALE_DEED",
  PROPERTY_DEED = "PROPERTY_DEED",
  VEHICLE_REGISTRATION_CERTIFICATE = "VEHICLE_REGISTRATION_CERTIFICATE",
  CERSAI = "CERSAI",
  JEWELLERY_CERTIFICATE = "JEWELERY_CERTIFICATE",
}


export const IdentityProfileDocumentTypeLabel: Record<
  IdentityProfileDocumentType,
  string
> = {
  [IdentityProfileDocumentType.PAN]: "PAN Card",
  [IdentityProfileDocumentType.AADHAAR]: "Aadhaar Card",
  [IdentityProfileDocumentType.VOTER_ID]: "Voter ID",
  [IdentityProfileDocumentType.PASSPORT]: "Passport",
  [IdentityProfileDocumentType.DRIVING_LICENSE]: "Driving License",
  [IdentityProfileDocumentType.UDYAM_CERTIFICATE]: "Udyam Certificate",
  [IdentityProfileDocumentType.CREDIT_REPORT]: "Credit Report",
  [IdentityProfileDocumentType.BANK_STATEMENT]: "Bank Statement",
  [IdentityProfileDocumentType.BANK_ACCOUNT]: "Bank Account Proof",
  [IdentityProfileDocumentType.EMPLOYMENT]: "Employment Proof",
  [IdentityProfileDocumentType.INCOME]: "Income Proof",
  [IdentityProfileDocumentType.RESIDENCE_ADDRESS]: "Residence Address Proof",
  [IdentityProfileDocumentType.BUSINESS_ADDRESS]: "Business Address Proof",
  [IdentityProfileDocumentType.RATION_CARD]: "Ration Card",
  [IdentityProfileDocumentType.UAN]: "UAN",
  [IdentityProfileDocumentType.EPFO]: "EPFO",
  [IdentityProfileDocumentType.ITR]: "ITR",
  [IdentityProfileDocumentType.FSSAI]: "FSSAI",
  [IdentityProfileDocumentType.CERSAI]: "CERSAI",
  [IdentityProfileDocumentType.BUILDING_SANCTION_PLAN]: "Building Sanction Plan",
  [IdentityProfileDocumentType.TRADE_LICENSE]: "Trade License",
  [IdentityProfileDocumentType.GST]: "GST Certificate",
  [IdentityProfileDocumentType.ELECTRICITY_BILL]: "Electricity Bill",
  [IdentityProfileDocumentType.AS26]: "26AS",  
  [IdentityProfileDocumentType.CIN]: "CIN",
  [IdentityProfileDocumentType.RENT_AGREEMENT]: "Rent Agreement",
  [IdentityProfileDocumentType.CA_CERTIFICATE]: "CA Certificate",
  [IdentityProfileDocumentType.SHOP_ACT_CERTIFICATE]: "Shop Act Certificate",
  [IdentityProfileDocumentType.MCA]: "MCA",
};

export enum VerificationStatus {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
  WAIVED = "WAIVED",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}


const VerificationAPI = {
    update: async (caseId: string, loanRefId: string, group: VerificationGroupType, id: string, request: UpdateVerificationRequest) => {
        const response: AxiosResponse<MessageResponse> = await axiosInstance.patch(`/v1/cases/${caseId}/verifications/loans/${loanRefId}/groups/${group}/ids/${id}`, request);
        return response.data;
    },

    add: async ( caseId: string, request: AddVerificationsRequest) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.post(
        `/v1/cases/${caseId}/verifications`,
        request
      );
    return response.data;
  },
}

export default VerificationAPI;